import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authenticatedApi } from '../services/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../hooks/useApi';

const I18nContext = createContext();

export const useI18n = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};

export const I18nProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [language, setLanguage] = useState(() => {
    // First try to get language from user's preferredLanguage in sessionStorage
    try {
      const userData = sessionStorage.getItem('inchaa_user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.preferredLanguage) {
          return user.preferredLanguage;
        }
      }
    } catch (error) {
      console.error('Error parsing user data for language:', error);
    }
    
    // Fallback to localStorage or default to English
    return localStorage.getItem('language') || 'en';
  });

  const [isRTL, setIsRTL] = useState(language === 'ar');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Stable callback: avoids new context value every render (prevents consumers like
  // PostLoginLanguageSwitcher from re-running effects in a tight loop / flicker).
  const syncLanguageFromUser = useCallback(() => {
    try {
      const userData = sessionStorage.getItem('inchaa_user');
      if (userData) {
        const user = JSON.parse(userData);
        if (user.preferredLanguage && ['en', 'ar'].includes(user.preferredLanguage)) {
          setLanguage((prev) =>
            prev !== user.preferredLanguage ? user.preferredLanguage : prev
          );
        }
      }
    } catch (error) {
      console.error('Error syncing language from user data:', error);
    }
  }, []);

  useEffect(() => {
    // Update RTL based on language
    setIsRTL(language === 'ar');
    
    // Update document direction and lang
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    
    // Store in localStorage for persistence
    localStorage.setItem('language', language);
  }, [language]);

  // Monitor sessionStorage / localStorage and poll periodically (stable handlers — no stale `language` closure).
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'inchaa_user') {
        syncLanguageFromUser();
      }
      if (e.key === 'language') {
        const newLanguage = e.newValue;
        if (newLanguage && ['en', 'ar'].includes(newLanguage)) {
          setLanguage((prev) => (prev !== newLanguage ? newLanguage : prev));
        }
      }
    };

    const handleLanguageChanged = (e) => {
      const newLanguage = e.detail?.language;
      if (newLanguage && ['en', 'ar'].includes(newLanguage)) {
        setLanguage((prev) => (prev !== newLanguage ? newLanguage : prev));
      }
    };

    const interval = setInterval(syncLanguageFromUser, 1000);
    syncLanguageFromUser();

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChanged', handleLanguageChanged);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChanged', handleLanguageChanged);
      clearInterval(interval);
    };
  }, [syncLanguageFromUser]);

  const switchLanguage = useCallback(
    async (newLanguage) => {
      if (!['en', 'ar'].includes(newLanguage)) return;
      try {
        await authenticatedApi.updateLanguage(newLanguage);

        localStorage.setItem('language', newLanguage);

        try {
          const userData = sessionStorage.getItem('inchaa_user') || localStorage.getItem('inchaa_user');
          if (userData) {
            const user = JSON.parse(userData);
            user.preferredLanguage = newLanguage;
            sessionStorage.setItem('inchaa_user', JSON.stringify(user));
            localStorage.setItem('inchaa_user', JSON.stringify(user));
          }
        } catch (error) {
          console.error('Error updating user data with new language:', error);
        }

        setLanguage(newLanguage);

        queryClient.invalidateQueries({
          queryKey: queryKeys.userProfile,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.dashboardStats,
        });

        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        console.error('Failed to update language on server:', error);
        localStorage.setItem('language', newLanguage);
        try {
          const userData = sessionStorage.getItem('inchaa_user') || localStorage.getItem('inchaa_user');
          if (userData) {
            const user = JSON.parse(userData);
            user.preferredLanguage = newLanguage;
            sessionStorage.setItem('inchaa_user', JSON.stringify(user));
            localStorage.setItem('inchaa_user', JSON.stringify(user));
          }
        } catch (e) {
          console.error('Error updating user data with new language:', e);
        }
        setLanguage(newLanguage);
        queryClient.invalidateQueries({
          queryKey: queryKeys.userProfile,
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.dashboardStats,
        });
        setRefreshTrigger((prev) => prev + 1);
      }
    },
    [queryClient]
  );

  const value = useMemo(
    () => ({
      language,
      switchLanguage,
      isRTL,
      syncLanguageFromUser,
      refreshTrigger,
    }),
    [language, switchLanguage, isRTL, syncLanguageFromUser, refreshTrigger]
  );

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};